import { isRadiusInRange, RADIUS_RANGE_MESSAGE, type FieldCircle } from '@/lib/maps/circle';
import { isStrictlyInsideAnyField, type FieldTarget } from '@/lib/maps/hit-test';
import type { MapPoint } from '@/lib/maps/types';

import {
  FIELD_ERROR_CODES,
  toWireCircle,
  toWirePoint,
  type BoundaryValidation,
  type PlacementVerdict,
  type ValidateBoundaryBody,
} from './schemas';

/** The backend's own copy, mirrored so the two sides cannot drift. `OVERLAP_MESSAGE` is also what
 *  the local pre-filter says, since it answers the same question without asking. */
export const USA_FALLBACK_MESSAGE = 'Field must be within the USA.';
export const OVERLAP_MESSAGE = 'Cannot mark on another field.';
export const DUPLICATE_POINT_MESSAGE = 'Coordinates already used for another point.';
/** No criterion covers a failed check; silence would leave corners mysteriously failing to appear
 *  (ADR-0018 § Consequences). House voice, matching `RegionError`'s copy. */
export const PLACEMENT_CHECK_FAILED_MESSAGE = "We couldn't check that location. Please try again.";

/**
 * A **Placement verdict**, reduced to what the map surface does with it: plot, or refuse in a tone
 * with a sentence. Deliberately not the twin's five-way `reason` union — nothing here branches on
 * *why*, only on *how loudly*. Ticket 09's coordinate rows add the `inlineMessage` they need.
 */
export type PlacementCheck =
  | { ok: true }
  | {
      ok: false;
      tone: 'error' | 'warning';
      /** The map surface's wording — what a toast says. */
      message: string;
      /**
       * The coordinate row's wording — what an inline error says. Carried on **every** refusal
       * rather than resolved per call site, so a toast reads `.message`, a row reads
       * `.inlineMessage`, and neither has to know the other exists.
       */
      inlineMessage: string;
    };

/** The Figma frames settle this: the USA refusal is the red `XCircle` toast (`594:534496`) and the
 *  overlap refusal the amber `Warning` one (`594:533926`). Everything else — a duplicate, a bad
 *  ring, a failed call — is a placement refusal, not a system error, so it takes the same warning
 *  tone as overlap. */
const toneFor = (errorCode: string | null): 'error' | 'warning' =>
  errorCode === FIELD_ERROR_CODES.outsideUsa ? 'error' : 'warning';

/**
 * What to say when a refusal arrives with no `message` of its own — which normal operation never
 * does, since the backend maps every code to a non-empty string.
 *
 * Keyed on the CODE rather than on which verdict raised it. A per-call-site fallback reads simpler
 * but is wrong the moment the assumption behind it breaks: an overlap or duplicate verdict reaching
 * the per-**Point** branch with a null message would have told the Grower "Field must be within the
 * USA." — the wrong reason, and one the Grower cannot act on.
 */
const FALLBACK_MESSAGES: Record<string, string> = {
  [FIELD_ERROR_CODES.outsideUsa]: USA_FALLBACK_MESSAGE,
  [FIELD_ERROR_CODES.overlapsExisting]: OVERLAP_MESSAGE,
  [FIELD_ERROR_CODES.duplicatePoint]: DUPLICATE_POINT_MESSAGE,
};

/** A refusal raised locally rather than by a Placement verdict — the pre-filter, the range check,
 *  and a failed round trip. Same voice in both places: nothing here is worth a row-specific
 *  wording, so `inlineMessage` just repeats `message`. */
const refusal = (tone: 'error' | 'warning', message: string): PlacementCheck => ({
  ok: false,
  tone,
  message,
  inlineMessage: message,
});

/** An unrecognised code with no message means the server refused and we cannot say why — which is
 *  what this copy says. Not reachable through any code the backend defines today. */
export const refuseVerdict = (verdict: PlacementVerdict): PlacementCheck => {
  const message =
    verdict.message || FALLBACK_MESSAGES[verdict.errorCode ?? ''] || PLACEMENT_CHECK_FAILED_MESSAGE;
  return {
    ok: false,
    tone: toneFor(verdict.errorCode),
    message,
    // The USA rule is the only one where the two voices differ, so everything else falls back to
    // the one string the server always sends.
    inlineMessage: verdict.inlineMessage || message,
  };
};

/**
 * May this **Point** go here?
 *
 * Two authorities, and the split is ADR-0018's: the **server** answers USA containment, overlap,
 * duplicate coordinates and the ring, and **we read all of them**; the client only ever *pre-empts*
 * an obvious refusal, never overrules one.
 *
 * The order below is the whole design.
 *
 * 1. **The optimistic pre-filter.** A click inside a Boundary the Grower can already see is refused
 *    instantly and costs no round trip. It uses {@link isStrictlyInsideAnyField} — the
 *    **exclusive** edge — so it can never refuse something the server would accept. It cannot
 *    collide with USA precedence either: every saved Field is USA-contained by construction, so a
 *    point inside one is inside the USA.
 * 2. **One round trip carrying the WHOLE candidate shape** — the array exactly as it would stand if
 *    the change were accepted. Sending only the candidate would be cheaper by nothing (containment
 *    is batched) and would throw away the `shape` verdict, which is the one no per-vertex pass can
 *    produce.
 * 3. **`results` first, in order.** The server evaluates USA → overlap → duplicate per **Point**,
 *    first failure winning, and reports only the USA failure when a shape is both outside the
 *    country and overlapping — so taking the FIRST invalid verdict reproduces that precedence for
 *    free. Every earlier **Point** in the array has already been accepted once, so in practice the
 *    first invalid one IS the candidate.
 * 4. **Then the ring.** `shape` is null below 3 **Point**s; `FIELD_SHAPE_NOT_EVALUATED` means a
 *    vertex already failed and the ring was never tested, and it carries an empty message, so it
 *    must never become the reason shown.
 *
 * `validate` is the caller's own `useValidateBoundary().mutateAsync`, taken as a plain function so
 * this module needs no React and no query client to test.
 */
export async function checkPlacement(
  validate: (body: ValidateBoundaryBody) => Promise<BoundaryValidation>,
  candidate: MapPoint,
  /**
   * The **whole candidate shape**, in marked order, INCLUDING `candidate` — not the committed
   * shape plus a candidate.
   *
   * A move must be validated as the shape it would produce: `points.with(index, candidate)` keeps
   * the ring's own order, where an "everything else, then the candidate" body would ask the server
   * about a ring nobody drew and get a `shape` verdict for it. An append passes
   * `[...points, candidate]` and is unchanged by this.
   */
  shape: readonly MapPoint[],
  existingFields: readonly FieldTarget[],
  /** `null` when creating, the Field's id when editing. Required, never defaulted — this is where
   *  the body is built, and a silent `null` is exactly how a self-conflict bug comes back. */
  excludeFieldId: string | null,
): Promise<PlacementCheck> {
  if (isStrictlyInsideAnyField({ x: candidate.lon, y: candidate.lat }, existingFields)) {
    return refusal('warning', OVERLAP_MESSAGE);
  }

  let verdict: BoundaryValidation;
  try {
    verdict = await validate({
      shapeType: 'polygon',
      points: shape.map(toWirePoint),
      excludeFieldId,
    });
  } catch {
    return refusal('warning', PLACEMENT_CHECK_FAILED_MESSAGE);
  }

  const failed = verdict.results.find((result) => !result.valid);
  if (failed) return refuseVerdict(failed);

  const { shape: ringVerdict } = verdict;
  if (
    ringVerdict &&
    !ringVerdict.valid &&
    ringVerdict.errorCode !== FIELD_ERROR_CODES.shapeNotEvaluated
  ) {
    return refuseVerdict(ringVerdict);
  }

  return { ok: true };
}

/**
 * May this **circle** go here?
 *
 * The circle sibling of {@link checkPlacement}, and the same two authorities in the same order —
 * with one rule moved across, for a reason in the backend rather than in taste: the **Radius range
 * is the client's**, because `CircleDto` carries `@Min`/`@Max` on `radiusFt` and rejects an
 * out-of-range value in its validation pipe. An out-of-range radius sent to the server comes back a
 * 422, not a verdict — so it is checked here, first, and never sent.
 *
 * After that:
 *
 * 1. **The optimistic pre-filter**, on the **Mid Point** only. Strictly weaker than the server,
 *    which samples the whole rim — which is what makes it safe: it can pre-empt a Mid Point dropped
 *    straight into a visible Boundary, and can never refuse what the server would accept.
 * 2. **One round trip carrying the circle itself** (grilling decision 15). The server materialises
 *    the ring and samples the Mid Point plus the rim, so the geometry it judges is the geometry it
 *    built.
 * 3. **`circle.midPoint`, then `circle.radius`.** That order is USA precedence: the server evaluates
 *    containment over every sample before it asks about overlap, and reports the one verdict — so
 *    "a circle both outside the USA and overlapping shows only the USA message, and the overlap
 *    surfaces once it is back inside" needs no rule here at all. `radius` is read second and in
 *    practice always valid (see `circleVerdictSchema`), rather than dropped.
 *
 * A reply with no `circle` verdict is treated as acceptance, exactly as a null `shape` is in
 * {@link checkPlacement}: the server always sends one for a circle request, and inventing a refusal
 * for a shape nothing refused would be worse than trusting it.
 */
export async function checkCirclePlacement(
  validate: (body: ValidateBoundaryBody) => Promise<BoundaryValidation>,
  circle: FieldCircle,
  existingFields: readonly FieldTarget[],
  /** `null` when creating, the Field's id when editing. Required, never defaulted — same reason as
   *  {@link checkPlacement}'s. */
  excludeFieldId: string | null,
): Promise<PlacementCheck> {
  if (!isRadiusInRange(circle.radiusFt)) {
    return refusal('error', RADIUS_RANGE_MESSAGE);
  }

  if (
    isStrictlyInsideAnyField({ x: circle.midPoint.lon, y: circle.midPoint.lat }, existingFields)
  ) {
    return refusal('warning', OVERLAP_MESSAGE);
  }

  let verdict: BoundaryValidation;
  try {
    verdict = await validate({
      shapeType: 'circle',
      circle: toWireCircle(circle),
      excludeFieldId,
    });
  } catch {
    return refusal('warning', PLACEMENT_CHECK_FAILED_MESSAGE);
  }

  const { circle: circleVerdict } = verdict;
  if (!circleVerdict) return { ok: true };
  if (!circleVerdict.midPoint.valid) return refuseVerdict(circleVerdict.midPoint);
  if (!circleVerdict.radius.valid) return refuseVerdict(circleVerdict.radius);

  return { ok: true };
}

/**
 * **Which single refusal a whole-shape reply produces** — the **Placement verdict** for a released
 * shape, reduced to one sentence in one tone.
 *
 * Pure, and the ONLY thing in this file that is: no round trip, no pre-filter, no `existingFields`.
 * That is what lets it be driven directly in the fast `node` project, where the precedence rule —
 * the part that is easy to get subtly wrong and impossible to see in a page test — is asserted as
 * arithmetic.
 *
 * **Precedence is derived here, client-side, in one pass, and never by a second request.** The AC
 * wants USA over overlap; the server gives per-**Point** precedence but says nothing about which of
 * *several* failing Points wins, and for a whole shape they can differ — Point 0 overlapping and
 * Point 3 outside the country is an ordinary release. So:
 *
 * 1. any `FIELD_OUTSIDE_USA`, wherever it sits → "Field must be within the USA.";
 * 2. else any `FIELD_OVERLAPS_EXISTING` → "Cannot mark on another field.";
 * 3. else the **first** invalid verdict's own message, whatever the server called it.
 *
 * That one rule satisfies **both** halves of the criterion, including _"once the shape falls
 * entirely within the USA, the overlap warning is then displayed if the overlap remains"_ — not
 * because anything remembers the earlier refusal, but because **each release re-derives** and the
 * USA verdict is simply no longer in the reply.
 *
 * `FIELD_SHAPE_NOT_EVALUATED` is skipped rather than ranked: it means a vertex already failed and
 * the ring was never tested, and it carries an **empty** message, so surfacing it would refuse the
 * Grower with a blank reason. Same rule {@link checkPlacement} applies to the same code.
 *
 * Deliberately **not** {@link checkPlacement}: that function pre-filters one candidate with
 * `isStrictlyInsideAnyField` and takes the first invalid verdict. Here the client must not pre-empt
 * anything — an edge merely TOUCHING a saved Field's boundary line is accepted, and that is the
 * server's call to make (ADR-0018; the admin twin's live defect is a client that answered it
 * itself).
 */
export function resolveShapeVerdicts(validation: BoundaryValidation): PlacementCheck {
  let outsideUsa: PlacementVerdict | null = null;
  let overlaps: PlacementVerdict | null = null;
  let first: PlacementVerdict | null = null;

  const consider = (verdict: PlacementVerdict | null) => {
    if (!verdict || verdict.valid) return;
    if (verdict.errorCode === FIELD_ERROR_CODES.shapeNotEvaluated) return;
    first ??= verdict;
    if (verdict.errorCode === FIELD_ERROR_CODES.outsideUsa) outsideUsa ??= verdict;
    if (verdict.errorCode === FIELD_ERROR_CODES.overlapsExisting) overlaps ??= verdict;
  };

  validation.results.forEach(consider);
  consider(validation.shape);

  const reason = outsideUsa ?? overlaps ?? first;
  return reason ? refuseVerdict(reason) : { ok: true };
}

/**
 * May the whole shape sit here? **One `validate-boundary` per drag release**, the full point array,
 * one round trip whatever the vertex count — containment is batched server-side.
 *
 * **`excludeFieldId` is `null`, always and by construction.** This flow only ever *creates*: there
 * is no Field yet, so there is nothing to exclude. Unlike {@link checkPlacement} it is not a
 * parameter, because a caller able to pass one is a caller able to pass the wrong one.
 *
 * **No local pre-filter.** {@link checkPlacement}'s exists to spare a round trip on an obvious
 * refusal for a single clicked **Point**; a shape can overlap a Field with no vertex inside it, the
 * criterion here is _"each drag release fires **one** call"_, and boundary-line contact must be the
 * server's answer. So the reply is the whole of the judgement.
 *
 * A failed call refuses the release in the warning tone with {@link PLACEMENT_CHECK_FAILED_MESSAGE},
 * the same posture the marking flow takes (ADR-0018 § Consequences): silence would leave the shape
 * mysteriously snapping back.
 */
export async function checkShapePlacement(
  validate: (body: ValidateBoundaryBody) => Promise<BoundaryValidation>,
  shape: readonly MapPoint[],
): Promise<PlacementCheck> {
  let validation: BoundaryValidation;
  try {
    validation = await validate({
      shapeType: 'polygon',
      points: shape.map(toWirePoint),
      excludeFieldId: null,
    });
  } catch {
    return refusal('warning', PLACEMENT_CHECK_FAILED_MESSAGE);
  }
  return resolveShapeVerdicts(validation);
}
